import { palette } from '@primeuix/themes';

import type { AccentPreset } from './appearance-presets';

/**
 * Accent colour math for Preferences -> Appearance: hex <-> sRGB <-> OKLab/OKLCH,
 * WCAG contrast, and the token family one accent colour expands into.
 *
 * Ported from MeetingRec's `appearance/accentColor.ts` (without its custom
 * brand-colour path, which PrimeControl does not offer). Pure functions, no
 * Angular imports. The OKLab matrices are from Bjorn Ottosson,
 * https://bottosson.github.io/posts/oklab/.
 */

export interface Oklch {
  l: number;
  c: number;
  h: number;
}

/** "#rrggbb" -> [r, g, b] in 0..255. */
export function hexToRgb(hex: string): [number, number, number] {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) throw new Error(`Not a #rrggbb colour: ${hex}`);
  const value = parseInt(match[1], 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

/** [r, g, b] in 0..255 -> "#rrggbb" (lowercase). */
export function rgbToHex([r, g, b]: readonly number[]): string {
  return `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

const toLinear = (channel: number): number => {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

const fromLinear = (value: number): number => {
  const c = value <= 0.0031308 ? 12.92 * value : 1.055 * value ** (1 / 2.4) - 0.055;
  return Math.round(Math.min(1, Math.max(0, c)) * 255);
};

function linearRgbToOklab([r, g, b]: readonly number[]): [number, number, number] {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

function oklabToLinearRgb([L, a, b]: readonly number[]): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

/** "#rrggbb" -> { l, c, h } in OKLCH (h in degrees, 0 when achromatic). */
export function hexToOklch(hex: string): Oklch {
  const [L, a, b] = linearRgbToOklab(hexToRgb(hex).map(toLinear));
  const c = Math.hypot(a, b);
  const h = c < 1e-6 ? 0 : ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
  return { l: L, c, h };
}

/** "#rrggbb" -> [L, a, b] in OKLab. */
export function hexToOklab(hex: string): [number, number, number] {
  return linearRgbToOklab(hexToRgb(hex).map(toLinear));
}

function oklchToLinearRgb({ l, c, h }: Oklch): [number, number, number] {
  const radians = (h * Math.PI) / 180;
  return oklabToLinearRgb([l, c * Math.cos(radians), c * Math.sin(radians)]);
}

const IN_GAMUT_EPSILON = 1e-4;
const isInGamut = (linear: readonly number[]): boolean =>
  linear.every((value) => value >= -IN_GAMUT_EPSILON && value <= 1 + IN_GAMUT_EPSILON);

/**
 * OKLCH -> "#rrggbb", lowering chroma (never lightness or hue) until the
 * colour fits sRGB, so a swapped hue keeps the source's brightness.
 */
export function oklchToHex({ l, c, h }: Oklch): string {
  let chroma = c;
  if (!isInGamut(oklchToLinearRgb({ l, c: chroma, h }))) {
    let low = 0;
    let high = c;
    for (let step = 0; step < 32; step++) {
      const mid = (low + high) / 2;
      if (isInGamut(oklchToLinearRgb({ l, c: mid, h }))) low = mid;
      else high = mid;
    }
    chroma = low;
  }
  return rgbToHex(oklchToLinearRgb({ l, c: chroma, h }).map(fromLinear));
}

/** [L, a, b] in OKLab -> "#rrggbb", fitted into sRGB the way `oklchToHex` does. */
export function oklabToHex([l, a, b]: readonly number[]): string {
  const c = Math.hypot(a, b);
  const h = c < 1e-6 ? 0 : ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
  return oklchToHex({ l, c, h });
}

/**
 * `source` with its hue replaced by `hue`; `achromatic` drops the chroma
 * entirely (the Graphite preset).
 */
export function swapHue(source: string, hue: number, achromatic = false): string {
  const { l, c } = hexToOklch(source);
  return oklchToHex({ l, c: achromatic ? 0 : c, h: hue });
}

/** WCAG 2.x relative luminance of "#rrggbb". */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(toLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.x contrast ratio between two "#rrggbb" colours (1..21). */
export function contrastRatio(first: string, second: string): number {
  const [lighter, darker] = [relativeLuminance(first), relativeLuminance(second)].sort(
    (x, y) => y - x
  );
  return (lighter + 0.05) / (darker + 0.05);
}

/** "#rrggbb" -> "r, g, b", the shape the `--*-rgb` tokens use. */
export function hexToRgbTriplet(hex: string): string {
  return hexToRgb(hex).join(', ');
}

/**
 * Obsidian's own values for every tone an accent derives. Each preset takes its
 * soft, ink and border tones from these by an OKLCH hue swap, keeping
 * obsidian's lightness in its own hue. The night surfaces are built
 * differently: see `readableNightRamp`.
 */
export const OBSIDIAN_SOURCE = {
  soft: '#dcd2ff',
  inkLight: '#3a2f7a',
  transcriptBorderLight: '#b3a8ea',
  transcriptBorderDark: '#4c4390',
  night1: '#2a2554',
  night2: '#181534',
  night3: '#08061c',
  nightLit1: '#3b3572',
  nightLit2: '#26214d',
  nightLit3: '#120f2e',
  nightShadow: '#0d0b26',
  glowLight: '#9682f5',
};

export const WHITE = '#ffffff';
/** The dark text used when white can't be read on an accent. */
export const DARK_TEXT = '#09090b';
/** The highest dark-mode surface a primary button sits on (the dialog background). */
export const DARK_DIALOG = '#1e1e22';
/**
 * How far a night surface must stand off the dark dialog to read as a filled
 * button rather than as more dialog. The chromatic presets land at ~2.1:1.
 */
export const NIGHT_SEPARATION = 2;

/** Text colour for an accent fill: white when it reads at 4.5:1, dark ink otherwise. */
export function contrastTextFor(accent: string): string {
  return contrastRatio(WHITE, accent) >= 4.5 ? WHITE : DARK_TEXT;
}

export type NightStop = 'night1' | 'night2' | 'night3' | 'nightLit1' | 'nightLit2' | 'nightLit3';
export type NightRamp = Record<NightStop, string>;

/**
 * OKLCH lightness offsets of each night stop from the ramp's middle, measured
 * on obsidian's own ramp (#2a2554 / #181534 / #08061c and its lit variants).
 */
const NIGHT_OFFSETS: Record<NightStop, number> = {
  night1: 0.06,
  night2: 0,
  night3: -0.13,
  nightLit1: 0.12,
  nightLit2: 0.05,
  nightLit3: -0.07,
};

/**
 * The same stops squeezed to a fifth of the spread, for a preset flagged
 * `flatNight`. The whole ramp is held down by its lightest stop (the lit
 * hover), so a narrow spread lets the rest sit near the white-text limit
 * instead of sinking 0.13 below it, which for yellow reads as brown-black.
 */
export const FLAT_NIGHT_OFFSETS: Record<NightStop, number> = {
  night1: 0.015,
  night2: 0,
  night3: -0.03,
  nightLit1: 0.025,
  nightLit2: 0.01,
  nightLit3: -0.015,
};

/** A hair above AA so rounding a stop to hex never dips below 4.5:1. */
const WHITE_TEXT_TARGET = 4.6;

/** What a token family is built from: an accent preset. */
export interface AccentSource {
  id: string;
  light: string;
  dark: string;
  /** A shallow night ramp, for hues that turn brown as they darken. */
  flatNight?: boolean;
  /** A trial floor below AA for white text on the fills. */
  whiteTextMin?: number;
  /** A grey: every derived tone drops its chroma, as Graphite's do. */
  achromatic?: boolean;
}

const nightOffsetsFor = (source: AccentSource) =>
  source.flatNight ? FLAT_NIGHT_OFFSETS : NIGHT_OFFSETS;
/** The white-text target for a source; a `whiteTextMin` trial preset uses its own. */
const whiteTextTargetFor = (source: AccentSource) => source.whiteTextMin ?? WHITE_TEXT_TARGET;

/**
 * The night surfaces (primary buttons, active sidebar item, dark tiles) for a
 * chromatic accent: the accent itself, darkened only as far as white text needs
 * to read at AA on every stop of the gradient.
 *
 * Swapping obsidian's own lightness onto other hues looked black: obsidian's
 * body sits at OKLCH L ~ 0.21, which reads as deep indigo but turns Amber, Lime
 * or Orange into a near-black brown. Starting from the accent keeps the hue
 * visible while the text stays readable.
 */
function readableNightRamp(
  accent: string,
  offsets = NIGHT_OFFSETS,
  target = WHITE_TEXT_TARGET
): NightRamp {
  const { l, c, h } = hexToOklch(accent);
  const rampAt = (middle: number) =>
    Object.fromEntries(
      Object.entries(offsets).map(([stop, offset]) => [
        stop,
        oklchToHex({ l: Math.max(0, middle + offset), c, h }),
      ])
    ) as NightRamp;
  for (let middle = l; middle > 0.05; middle -= 0.005) {
    const ramp = rampAt(middle);
    if (Object.values(ramp).every((stop) => contrastRatio(WHITE, stop) >= target)) return ramp;
  }
  throw new Error(`No readable night ramp for ${accent}`);
}

/**
 * The night surfaces for dark mode. Most presets reuse their light ramp: it is
 * already the accent's own hue at a lightness that stands off a dark dialog.
 * A preset whose light accent is itself near-black (Obsidian, Graphite, Navy)
 * has a ramp that disappears into the dark surfaces, so those rebuild the ramp
 * from the dark accent by the same rule.
 */
export function darkNightRamp(source: AccentSource, night: NightRamp): NightRamp {
  if (contrastRatio(night.night2, DARK_DIALOG) >= NIGHT_SEPARATION) return night;
  return readableNightRamp(source.dark, nightOffsetsFor(source), whiteTextTargetFor(source));
}

/**
 * A fill for content that is always white (avatar initials, count badges):
 * the accent itself when white reads on it, otherwise the lightest night stop.
 */
function solidFillFor(accent: string, night: NightRamp, minimum = 4.5): string {
  return contrastRatio(WHITE, accent) >= minimum ? accent : night.night1;
}

export interface AccentFamily {
  id: string;
  light: {
    accent: string;
    accentContrast: string;
    accentSolid: string;
    accentInk: string;
    transcriptBorder: string;
  };
  dark: {
    accent: string;
    accentContrast: string;
    accentSolid: string;
    transcriptBorder: string;
    night: NightRamp;
  };
  shared: NightRamp & { soft: string; nightShadow: string; glow: string; glowLight: string };
}

/** The full token family of one accent. */
export function buildFamily(source: AccentSource): AccentFamily {
  const achromatic = source.achromatic ?? source.id === 'graphite';
  const hue = hexToOklch(source.light).h;
  const swap = (hex: string) => swapHue(hex, hue, achromatic);
  // Graphite stays monochrome near-black, like obsidian's own ramp.
  const night: NightRamp = achromatic
    ? (Object.fromEntries(
        (Object.keys(NIGHT_OFFSETS) as NightStop[]).map((stop) => [
          stop,
          swap(OBSIDIAN_SOURCE[stop]),
        ])
      ) as NightRamp)
    : readableNightRamp(source.light, nightOffsetsFor(source), whiteTextTargetFor(source));
  return {
    id: source.id,
    light: {
      accent: source.light,
      accentContrast: contrastTextFor(source.light),
      accentSolid: solidFillFor(source.light, night, source.whiteTextMin),
      accentInk: swap(OBSIDIAN_SOURCE.inkLight),
      transcriptBorder: swap(OBSIDIAN_SOURCE.transcriptBorderLight),
    },
    dark: {
      accent: source.dark,
      accentContrast: contrastTextFor(source.dark),
      accentSolid: solidFillFor(source.dark, night, source.whiteTextMin),
      transcriptBorder: swap(OBSIDIAN_SOURCE.transcriptBorderDark),
      night: darkNightRamp(source, night),
    },
    shared: {
      soft: swap(OBSIDIAN_SOURCE.soft),
      ...night,
      nightShadow: swap(OBSIDIAN_SOURCE.nightShadow),
      // The glow is the vivid dark-mode accent in both themes.
      glow: source.dark,
      glowLight: swap(OBSIDIAN_SOURCE.glowLight),
    },
  };
}

/** The source a preset's token family is built from (Graphite is the one achromatic preset). */
export function accentSourceOf(preset: AccentPreset): AccentSource {
  return {
    id: preset.id,
    light: preset.light,
    dark: preset.dark,
    flatNight: preset.flatNight,
    whiteTextMin: preset.whiteTextMin,
    achromatic: preset.id === 'graphite',
  };
}

/* ------------------------------------------------------------------------ */
/* CSS variables                                                             */
/* ------------------------------------------------------------------------ */

/**
 * The accent-independent chrome tokens (icons, badges, dotted borders, active
 * sidebar item): today's neutral-grey defaults, applied under every accent.
 */
export const NEUTRAL_CHROME: { light: Record<string, string>; dark: Record<string, string> } = {
  light: {
    '--icon-color': '#6B7280a5',
    '--icon-item-color': '#4B5563d5',
    '--hover-item-border-color': '#6B728050',
    '--badge-bg': '#9ca3af2a',
    '--badge-text': '#374151cb',
    '--dotted-border-color': '#6B728040',
    '--dot-color': '#6B728030',
    '--text-select-tab': '#374151',
    '--sidebar-item-active-bg': 'rgba(156,163,175,0.18)',
    '--sidebar-item-active-border': 'rgba(156,163,175,0.38)',
  },
  dark: {
    '--icon-color': '#9CA3AF',
    '--icon-item-color': '#D1D5DBd5',
    '--hover-item-border-color': '#9CA3AF50',
    '--badge-bg': '#37415142',
    '--badge-text': '#E5E7EBcb',
    '--dotted-border-color': '#9CA3AF40',
    '--dot-color': '#9CA3AF20',
    '--text-select-tab': '#D1D5DB',
    '--sidebar-item-active-bg': 'rgba(156,163,175,0.18)',
    '--sidebar-item-active-border': 'rgba(156,163,175,0.38)',
  },
};

/** The ramp and button tokens of one scheme. */
interface AccentTokens {
  buttonBg: string;
  buttonHover: string;
  base: string;
  deep: string;
  deepest: string;
  ink: string;
}

/** Text/icon colour painted on accent-filled surfaces. */
const ACCENT_CONTRAST = '#f8fafc';
/** How much OKLCH lightness `--pc-accent-deepest` sits below `--pc-accent-deep`. */
const DEEPEST_LIGHTNESS_DROP = 0.08;

function darken(hex: string, lightnessDrop: number): string {
  const { l, c, h } = hexToOklch(hex);
  return oklchToHex({ l: Math.max(0, l - lightnessDrop), c, h });
}

function derivedTokens(preset: AccentPreset, scheme: 'light' | 'dark'): AccentTokens {
  const family = buildFamily(accentSourceOf(preset));
  const night = scheme === 'light' ? family.shared : family.dark.night;
  return {
    buttonBg: night.night2,
    buttonHover: scheme === 'light' ? night.night1 : night.nightLit2,
    base: night.night2,
    deep: night.night3,
    deepest: darken(night.night3, DEEPEST_LIGHTNESS_DROP),
    // Token contract: the legible accent on dark surfaces, in both schemes
    // (details-header paints it on a dark tile even in light mode).
    ink: preset.dark,
  };
}

/**
 * Every CSS custom property one accent paints under a colour scheme: the
 * neutral chrome, the primary-button trio, and the `--pc-accent-*` ramp (plus
 * `-rgb` triplets) the recipes read.
 */
export function accentCssVariables(
  preset: AccentPreset,
  scheme: 'light' | 'dark'
): Record<string, string> {
  const tokens = derivedTokens(preset, scheme);
  return {
    ...NEUTRAL_CHROME[scheme],
    '--main-button-bg': tokens.buttonBg,
    '--main-button-hover': tokens.buttonHover,
    '--main-button-text': WHITE,
    '--pc-accent-base': tokens.base,
    '--pc-accent-deep': tokens.deep,
    '--pc-accent-deepest': tokens.deepest,
    '--pc-accent-ink': tokens.ink,
    '--pc-accent-contrast': ACCENT_CONTRAST,
    '--pc-accent-base-rgb': hexToRgbTriplet(tokens.base),
    '--pc-accent-deep-rgb': hexToRgbTriplet(tokens.deep),
    '--pc-accent-deepest-rgb': hexToRgbTriplet(tokens.deepest),
    '--pc-accent-ink-rgb': hexToRgbTriplet(tokens.ink),
  };
}

/** The PrimeNG colour scale keys, lightest to darkest. */
export type PaletteStep =
  | '50'
  | '100'
  | '200'
  | '300'
  | '400'
  | '500'
  | '600'
  | '700'
  | '800'
  | '900'
  | '950';

export type PrimePalette = Record<PaletteStep | '0', string>;

/**
 * The PrimeNG `primary` scale for an accent: generated from the button colour
 * in light and from the accent ink in dark, with a white `0` step. Keys are
 * exactly the ones `palette()` generates; no steps are remapped. Every accent,
 * the default included, gets its own scale: the sidebar's hover and active
 * rows (and other `--p-primary-color` readers) follow the picked colour.
 */
export function primePalette(preset: AccentPreset, scheme: 'light' | 'dark'): PrimePalette {
  const vars = accentCssVariables(preset, scheme);
  const seed = scheme === 'light' ? vars['--main-button-bg'] : vars['--pc-accent-ink'];
  const scale = palette(seed) as Record<PaletteStep, string>;
  return { 0: WHITE, ...scale };
}
